import React from 'react';
import { Link } from 'react-router-dom';
import { useCart, formatPrice, parsePrice } from '../context/CartContext';
import { useT, useLanguage } from '../i18n/LanguageContext';
import { TYPE_KEYS } from '../i18n/translations';

/* ============================================================
   Carrinho — /carrinho
   ============================================================ */

export default function Carrinho() {
  const t = useT();
  const { lang } = useLanguage();
  const {
    items,
    totalItems,
    subtotal,
    subtotalFormatted,
    updateQty,
    removeItem,
    clearCart,
    isValidQuantity,
    unitsNeededForValidQty,
  } = useCart();

  const empty = items.length === 0;

  // Portes: preço fixo por conjunto de 6 garrafas, muito diferente consoante
  // o destino (Portugal vs. resto do mundo) — sem morada ainda escolhida
  // aqui, mostrar um valor arriscaria estar muito errado. O valor real só
  // aparece no checkout (ver Checkout.js), depois de o país ser escolhido.
  const total = subtotal;

  const localeForPrice = lang === 'en' ? 'en-GB' : 'pt-PT';

  /* ── Empty state ─────────────────────────────────────── */
  if (empty) {
    return (
      <div className="page-top">
        <section className="section" style={{ paddingTop: 'calc(var(--nav-height) + var(--sp-16))' }}>
          <div className="container--narrow t-center">
            <div className="cart-empty">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                <path
                  d="M14 18 H50 L46 44 H18 L14 18 Z"
                  stroke="var(--clr-ouro)"
                  strokeWidth="1.2"
                  fill="none"
                />
                <path
                  d="M22 18 C22 12 26 8 32 8 C38 8 42 12 42 18"
                  stroke="var(--clr-ouro)"
                  strokeWidth="1.2"
                  fill="none"
                />
                <circle cx="24" cy="52" r="3" stroke="var(--clr-ouro)" strokeWidth="1.2" fill="none" />
                <circle cx="42" cy="52" r="3" stroke="var(--clr-ouro)" strokeWidth="1.2" fill="none" />
              </svg>
              <p className="t-label" style={{ color: '#000000', marginTop: 'var(--sp-6)' }}>
                {t('cart.eyebrow')}
              </p>
              <h1 className="t-h2" style={{ marginTop: 'var(--sp-3)' }}>
                {t('cart.emptyTitle')}
              </h1>
              <p className="t-body-lg t-light" style={{ marginTop: 'var(--sp-5)', marginBottom: 'var(--sp-10)' }}>
                {t('cart.emptyLead')}
              </p>
              <Link to="/loja" className="btn btn-lg">
                {t('cart.emptyCta')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  /* ── Carrinho com itens ──────────────────────────────── */
  return (
    <div className="page-top">
      <section
        className="section--sm page-header"
        style={{ background: 'var(--clr-azul-veludo)', color: '#ffffff' }}
      >
        <div className="container">
          <p className="t-label" style={{ color: '#ffffff', marginBottom: 'var(--sp-4)' }}>
            {t('cart.eyebrow')}
          </p>
          <h1 className="t-h1" style={{ color: '#ffffff' }}>{t('cart.title')}</h1>
          <p className="t-body" style={{ color: 'rgba(255,255,255,0.7)', marginTop: 'var(--sp-3)' }}>
            {totalItems} {totalItems === 1 ? t('cart.itemSingular') : t('cart.itemPlural')}
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="cart-layout">

            {/* ── Lista ── */}
            <div className="cart-list">
              {items.map(item => {
                const lineTotal = parsePrice(item.price) * item.qty;
                const typeLabel = TYPE_KEYS[item.type] ? t(TYPE_KEYS[item.type]) : item.type;
                return (
                  <article key={item.id} className="cart-item">
                    <div className="cart-item__image">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="cart-item__img" />
                      ) : (
                        <svg width="24" height="48" viewBox="0 0 32 64" fill="none" aria-hidden="true">
                          <path
                            d="M16 2 C8 10 4 20 4 28 C4 40 10 48 16 50 C22 48 28 40 28 28 C28 20 24 10 16 2Z"
                            stroke="var(--clr-ouro)"
                            strokeWidth="1"
                            fill="none"
                          />
                          <line x1="16" y1="50" x2="16" y2="62" stroke="var(--clr-ouro)" strokeWidth="1" />
                          <line x1="10" y1="62" x2="22" y2="62" stroke="var(--clr-ouro)" strokeWidth="1" />
                        </svg>
                      )}
                    </div>

                    <div className="cart-item__info">
                      <p className="card__eyebrow">{typeLabel} · {item.year}</p>
                      <h3 className="t-h4" style={{ marginTop: 'var(--sp-1)' }}>{item.name}</h3>
                      <p className="t-small t-light" style={{ marginTop: 'var(--sp-2)' }}>
                        {item.price} {t('cart.unit')}
                      </p>
                    </div>

                    <div className="cart-item__qty">
                      <button
                        type="button"
                        className="qty-btn"
                        onClick={() => updateQty(item.id, item.qty - 1)}
                        aria-label={t('cart.qtyDecrease')}
                      >
                        −
                      </button>
                      <span className="qty-value">{item.qty}</span>
                      <button
                        type="button"
                        className="qty-btn"
                        onClick={() => updateQty(item.id, item.qty + 1)}
                        aria-label={t('cart.qtyIncrease')}
                      >
                        +
                      </button>
                    </div>

                    <div className="cart-item__total">
                      <p className="card__price">{formatPrice(lineTotal, localeForPrice)}</p>
                      <button
                        type="button"
                        className="cart-item__remove"
                        onClick={() => removeItem(item.id)}
                        aria-label={`${t('cart.remove')} ${item.name}`}
                      >
                        {t('cart.remove')}
                      </button>
                    </div>
                  </article>
                );
              })}

              <div style={{ marginTop: 'var(--sp-6)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--sp-4)' }}>
                <Link to="/loja" className="btn btn-sm">
                  {t('cart.continueShopping')}
                </Link>
                <button
                  type="button"
                  onClick={clearCart}
                  className="t-small t-light"
                  style={{ textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none' }}
                >
                  {t('cart.clear')}
                </button>
              </div>
            </div>

            {/* ── Resumo ── */}
            <aside className="cart-summary">
              <p className="t-small t-center" style={{ marginBottom: 'var(--sp-5)' }}>
                {t('cart.comfortMessage')}
              </p>

              <h2 className="t-h4" style={{ marginBottom: 'var(--sp-6)' }}>{t('cart.summary')}</h2>

              <div className="cart-summary__row">
                <span className="t-body">{t('cart.subtotal')}</span>
                <span className="t-body">{subtotalFormatted}</span>
              </div>

              <div className="cart-summary__row">
                <span className="t-body">{t('cart.shipping')}</span>
                <span className="t-body t-light">{t('cart.shippingAtCheckout')}</span>
              </div>

              <div className="divider" style={{ margin: 'var(--sp-5) 0' }} />

              <div className="cart-summary__row cart-summary__row--total">
                <span className="t-h4">{t('cart.total')}</span>
                <span className="t-h4">{formatPrice(total, localeForPrice)}</span>
              </div>

              {!isValidQuantity && (
                <p className="form-error t-center" style={{ marginTop: 'var(--sp-6)' }}>
                  {t('cart.minQty.warning', { count: unitsNeededForValidQty })}
                </p>
              )}

              {isValidQuantity ? (
                <Link to="/checkout" className="btn btn-lg" style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--sp-8)' }}>
                  {t('cart.checkout')}
                </Link>
              ) : (
                <button
                  type="button"
                  className="btn btn-lg"
                  disabled
                  style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--sp-4)', opacity: 0.5, cursor: 'not-allowed' }}
                >
                  {t('cart.checkout')}
                </button>
              )}

              <p className="t-small t-light t-center" style={{ marginTop: 'var(--sp-4)' }}>
                {t('cart.taxesNote')}
              </p>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
